<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { useQuery } from '@tanstack/vue-query';
    import { useI18n } from 'vue-i18n';
    import axios from 'axios';
    import { UserCog } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import EditUserCompany from '@/components/user-companies/EditUserCompany.vue';
    import type { UserCompany } from '@/types';
    import { triggerDashboardTableRefetch } from '@/composables/dashboardTableRefresh';

    const props = defineProps<{
        companyId: number;
        refreshDashboard?: boolean;
    }>();

    const { t } = useI18n();
    const { hasPermission } = usePermissions();

    const canEdit = computed(() => hasPermission(Permission.COMPANIES_UPDATE));

    const editRequested = ref(false);
    const editOpen = ref(false);

    const { data: userCompany } = useQuery({
        queryKey: ['dashboard-user-company', props.companyId],
        queryFn: async (): Promise<UserCompany> => {
            const { data } = await axios.get(route('v2.user-companies.get', props.companyId));
            return data;
        },
        enabled: editRequested,
        gcTime: 0,
    });

    const openEdit = () => {
        if (userCompany.value) {
            editOpen.value = true;
            return;
        }

        editRequested.value = true;
    };

    watch(userCompany, (value) => {
        if (value && editRequested.value) {
            editOpen.value = true;
        }
    });

    // Capture the value now so the callback works even after this component
    // is unmounted by the Inertia page remount that follows a modal save.
    const shouldRefreshDashboard = props.refreshDashboard === true;

    const refreshDashboardTable = () => {
        if (!shouldRefreshDashboard) {
            return;
        }

        triggerDashboardTableRefetch({ forceHydration: true });
    };
</script>

<template>
    <div
        v-if="canEdit"
        class="flex w-6 shrink-0 items-center justify-end"
        @click.stop
    >
        <Button
            variant="ghost_primary"
            size="icon_sm"
            class="size-6 text-muted-foreground"
            :title="t('companies.user_companies_edit_title')"
            @click="openEdit"
        >
            <UserCog class="size-3.5" />
        </Button>

        <EditUserCompany
            v-if="userCompany"
            :user-company="userCompany"
            :open="editOpen"
            :after-success="refreshDashboardTable"
            hide-trigger
            @update:open="editOpen = $event"
        />
    </div>
</template>
